import type { ComponentType, ReactNode } from 'react';
import type { AclRoute } from './acl-routes';
import { usePermissions } from './use-permissions';
import { holdsAny } from './holds-any';
import { NotAuthorized } from './NotAuthorized';

export interface WithPermissionOptions {
  /** The UI permission-key the caller must hold to render the component. An array means ANY of them. */
  key: string | readonly string[];
  /** The ACL route whose key set to check. */
  route: AclRoute;
  /** Shown on the NotAuthorized page (e.g. "Clients"). */
  resourceLabel?: string;
  /** Rendered while keys are loading (default: `null`). Fail-closed — never the gated UI. */
  loadingFallback?: ReactNode;
  /** Override the denied fallback (default: <NotAuthorized resourceLabel />). */
  deniedFallback?: ReactNode;
}

/**
 * HOC gating a component behind a UI permission-key — intended for route
 * protection. FAIL-CLOSED: renders the gated component only once the key set has
 * resolved AND contains `key`; otherwise the loading fallback (while loading) or
 * the NotAuthorized page (on deny / error).
 *
 * ⚠️ UX only — the BE authorizes every real call regardless of what's rendered.
 *
 * @example
 * export const Route = createFileRoute('/clients')({
 *   component: withPermission(Outlet, {
 *     key: ClientAclKeys.listingRead, route: AclRoute.CLIENT, resourceLabel: 'Clients',
 *   }),
 * });
 */
export function withPermission<P extends object>(
  Wrapped: ComponentType<P>,
  { key, route, resourceLabel, loadingFallback = null, deniedFallback }: WithPermissionOptions,
): (props: P) => ReactNode {
  function PermissionGuarded(props: P) {
    const { isLoading, checkPermission } = usePermissions(route);
    if (isLoading) return <>{loadingFallback}</>;
    if (!holdsAny(checkPermission, key)) {
      return <>{deniedFallback ?? <NotAuthorized resourceLabel={resourceLabel} />}</>;
    }
    return <Wrapped {...props} />;
  }
  PermissionGuarded.displayName = `withPermission(${Wrapped.displayName ?? Wrapped.name ?? 'Component'})`;
  return PermissionGuarded;
}
